// Story 1.29 — admin platform metrics dashboard. Wrapped by <AdminGuard>
// in App.tsx.
//
// Tabbed dashboard with 9 panels, all wired: Users (MAU +
// signup funnel), Drops (sold + GMV + top drops), Marketplace (trades + GMV,
// opened-share vs sealed-dossier), Creator Earnings (cumulative/primary/
// resale + top-creator leaderboard), Reconciliation (Story 6.7), Tier
// Telemetry (configured vs actual per dossier), Founder's Multiple
// (secondary/primary), VAT Exposure (Story 11.2, 2026-09-01 — the per-country
// count of top-ups taken with no rule on file; turned into EUROS of cumulative
// exposure, the reserve held against it and the researched side's real
// vat_payable position by Story 11.9, 2026-09-02), and
// VAT Registration (Story 11.3, 2026-09-01 — the registration state in force,
// the two threshold counters that end it, and the audited control that changes
// it; ⛔ the state is never an env var, a config file or a deploy).
// The wired/placeholder envelope contract is retained
// so a future data source can still light up a panel without a re-scaffold.
// Tabs underline-style per UX-DR26 (the @greenhold/components `Tabs`
// primitive ships this styling baked in).

import { useState } from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@greenhold/components";

import { EmptyState } from "../components/EmptyState";
import { UsersPanel } from "../features/admin/UsersPanel";
import { useRecordVatRegistrationState } from "../features/admin/useRecordVatRegistrationState";
import {
  type CreatorEarningsPanelData,
  type DropsPanelData,
  type FoundersMultiplePanelData,
  type MarketplacePanelData,
  type PanelEnvelope,
  type ReconciliationPanelData,
  type TierTelemetryPanelData,
  type UnresearchedCountriesPanelData,
  type UsersPanelData,
  type VatRegistrationPanelData,
  type VatThresholdCounter,
  useAdminMetrics,
} from "../features/admin/useAdminMetrics";

function formatEur(cents: number): string {
  const euros = Math.floor(Math.abs(cents) / 100);
  const remainder = Math.abs(cents) % 100;
  return `${cents < 0 ? "-" : ""}€${euros}.${remainder.toString().padStart(2, "0")}`;
}

function formatRelative(iso: string): string {
  // Minimal "Nm ago / Nh ago / Nd ago" formatter — matches Story 1.27's
  // helper in AdminApplicationsPage.tsx. Avoids adding a date-fns dep.
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function isWired<T>(panel: PanelEnvelope<T>): panel is { data: T } {
  return "data" in panel;
}

function PlaceholderPanel({
  reason,
  story,
}: {
  reason: string;
  story: string;
}) {
  return (
    <Card>
      <CardContent className="p-8 text-center space-y-3">
        <Badge variant="secondary" className="font-mono">
          Coming with Story {story}
        </Badge>
        <p className="opacity-80 max-w-xl mx-auto leading-relaxed">{reason}</p>
        <p className="text-xs opacity-60 italic">
          No mock data is shown — this panel lights up when its source story
          ships.
        </p>
      </CardContent>
    </Card>
  );
}

function CreatorEarningsPanel({ data }: { data: CreatorEarningsPanelData }) {
  return (
    <div className="space-y-4" data-testid="creator-earnings-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: "Cumulative creator earnings", value: data.cumulative_eur_cents },
          { label: "From primary sales (50% of Net Proceeds)", value: data.primary_eur_cents },
          { label: "From resale royalties (5%)", value: data.resale_eur_cents },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wide opacity-70">
                {stat.label}
              </p>
              <p className="text-2xl font-heading font-bold tabular-nums mt-1">
                {formatEur(stat.value)}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-5">
          <h3 className="font-heading font-semibold mb-3">Top creators</h3>
          {data.top_creators.length === 0 ? (
            <EmptyState
              aria-label="No creator earnings"
              description="No creator earnings yet — rows appear when dossiers open or certificates resell."
            />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left opacity-70">
                  <th className="py-1 pr-4 font-medium">Creator</th>
                  <th className="py-1 pr-4 font-medium text-right">Primary</th>
                  <th className="py-1 pr-4 font-medium text-right">Resale</th>
                  <th className="py-1 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.top_creators.map((row) => (
                  <tr
                    key={row.creator_user_id}
                    className="border-t border-border"
                    data-testid="top-creator-row"
                  >
                    <td className="py-1.5 pr-4">
                      {row.display_name ?? "—"}
                      {row.handle ? (
                        <span className="text-brand-forest-700"> @{row.handle}</span>
                      ) : null}
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">
                      {formatEur(row.primary_eur_cents)}
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">
                      {formatEur(row.resale_eur_cents)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums font-semibold">
                      {formatEur(row.total_eur_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function formatLatency(ms: number): string {
  if (ms <= 0) return "—";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return `${Math.round(ms / 1000)}s`;
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function ReconciliationPanel({ data }: { data: ReconciliationPanelData }) {
  const run = data.latest_run;
  if (!run) {
    return (
      <EmptyState
        aria-label="No reconciliation runs"
        description="No reconciliation runs recorded yet — the hourly worker writes the first run within one interval of starting."
      />
    );
  }

  // Red while a drift is open; amber for a failed (couldn't-verify) run; green
  // for a clean pass.
  const tone = data.open_drift
    ? { badge: "destructive" as const, label: "DRIFT DETECTED" }
    : run.status === "failed"
      ? { badge: "secondary" as const, label: "RUN FAILED — wallets unverified" }
      : { badge: "secondary" as const, label: "Passed" };

  return (
    <div className="space-y-4" data-testid="reconciliation-panel">
      <Card
        className={data.open_drift ? "border-destructive" : undefined}
        data-drift={data.open_drift ? "true" : "false"}
      >
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Badge variant={tone.badge}>{tone.label}</Badge>
            <span className="text-xs font-mono opacity-70">
              last run {formatRelative(run.run_at)} · cadence{" "}
              {Math.round(data.interval_secs / 60)}m
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Open drift", value: formatEur(run.drift_cents) },
              {
                label: "In flight",
                value: `${run.in_flight_count} · ${formatEur(run.in_flight_cents)}`,
              },
              {
                label: "Unattributed txns",
                value: `${run.unmatched_external_count}`,
              },
              {
                label: "Clean-run streak",
                value: `${data.consecutive_clean_runs}`,
              },
              {
                label: "Worst in-flight lag",
                value: formatLatency(data.max_settlement_latency_ms),
              },
              {
                label: "Wallets checked",
                value: `${run.wallets_checked}`,
              },
              {
                label: "Wallets failed",
                value: `${run.wallets_failed}`,
              },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-xs uppercase tracking-wide opacity-70">
                  {stat.label}
                </p>
                <p className="text-lg font-heading font-semibold tabular-nums mt-0.5">
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {data.drifting_wallets.length > 0 ? (
        <Card>
          <CardContent className="p-5">
            <h3 className="font-heading font-semibold mb-3">
              Wallets needing attention
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left opacity-70">
                  <th className="py-1 pr-4 font-medium">Wallet</th>
                  <th className="py-1 pr-4 font-medium">Kind</th>
                  <th className="py-1 pr-4 font-medium">Status</th>
                  <th className="py-1 pr-4 font-medium text-right">Drift</th>
                  <th className="py-1 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.drifting_wallets.map((w) => (
                  <tr
                    key={w.wallet_ref}
                    className="border-t border-border"
                    data-testid="reconciliation-drift-row"
                  >
                    <td className="py-1.5 pr-4 font-mono text-xs">
                      {w.wallet_ref}
                    </td>
                    <td className="py-1.5 pr-4">{w.wallet_kind}</td>
                    <td className="py-1.5 pr-4">{w.status}</td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">
                      {formatEur(w.drift_cents)}
                    </td>
                    <td className="py-1.5 opacity-80">{w.detail ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function formatPct(fraction: number): string {
  if (!Number.isFinite(fraction)) return "—";
  const pct = fraction * 100;
  // Founder's odds run to 0.1%; keep 2 decimals under 1%, 1 decimal above.
  const digits = pct > 0 && pct < 1 ? 2 : 1;
  return `${pct.toFixed(digits)}%`;
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
        <p className="text-2xl font-heading font-bold tabular-nums mt-1">
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function DropsPanel({ data }: { data: DropsPanelData }) {
  const maxSold = Math.max(1, ...data.sold_by_month.map((r) => r.dossiers_sold));
  const allZero = data.cumulative_sold === 0;
  return (
    <div className="space-y-4" data-testid="drops-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatCard
          label="Dossiers sold (all time)"
          value={`${data.cumulative_sold}`}
        />
        <StatCard
          label="Primary GMV (all time)"
          value={formatEur(data.cumulative_gmv_eur_cents)}
        />
      </div>

      <Card>
        <CardContent className="p-5 space-y-3">
          <h3 className="font-heading font-semibold">
            Dossiers sold per month
          </h3>
          {allZero ? (
            <p className="text-sm opacity-70 italic">
              No primary dossier sales in the last 12 months yet.
            </p>
          ) : (
            <div className="space-y-1.5">
              {data.sold_by_month.map((row) => (
                <div
                  key={row.month}
                  className="flex items-center gap-3"
                  title={`${row.month}: ${row.dossiers_sold} sold · ${formatEur(row.gmv_eur_cents)}`}
                >
                  <span className="font-mono text-xs w-20 opacity-80">
                    {row.month}
                  </span>
                  <div className="flex-1 h-4 bg-brand-paper-100 rounded-sm overflow-hidden">
                    <div
                      style={{
                        width: `${(row.dossiers_sold / maxSold) * 100}%`,
                      }}
                      className="h-full bg-brand-forest-700 rounded-sm"
                    />
                  </div>
                  <span className="font-mono text-xs w-32 text-right tabular-nums opacity-80">
                    {row.dossiers_sold} · {formatEur(row.gmv_eur_cents)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h3 className="font-heading font-semibold mb-3">
            Top drops by units sold
          </h3>
          {data.top_drops.length === 0 ? (
            <EmptyState
              aria-label="No drops sold"
              description="No primary dossier sales yet — rows appear when a drop's dossiers sell."
            />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left opacity-70">
                  <th className="py-1 pr-4 font-medium">Drop</th>
                  <th className="py-1 pr-4 font-medium">Kind</th>
                  <th className="py-1 pr-4 font-medium">Status</th>
                  <th className="py-1 pr-4 font-medium text-right">Sold</th>
                  <th className="py-1 pr-4 font-medium text-right">GMV</th>
                  <th className="py-1 font-medium text-right">Open rate</th>
                </tr>
              </thead>
              <tbody>
                {data.top_drops.map((row) => (
                  <tr
                    key={row.drop_id}
                    className="border-t border-border"
                    data-testid="top-drop-row"
                  >
                    <td className="py-1.5 pr-4">{row.portfolio_title}</td>
                    <td className="py-1.5 pr-4 capitalize">{row.kind}</td>
                    <td className="py-1.5 pr-4 capitalize">{row.status}</td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">
                      {row.sold_count}
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">
                      {formatEur(row.gmv_eur_cents)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums opacity-80">
                      {row.sold_count > 0
                        ? formatPct(row.opened_count / row.sold_count)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MarketplacePanel({ data }: { data: MarketplacePanelData }) {
  const c = data.cumulative;
  const totalTrades = c.share_trades + c.sealed_trades;
  const totalGmv = c.share_gmv_eur_cents + c.sealed_gmv_eur_cents;
  const maxMonthTrades = Math.max(
    1,
    ...data.trades_by_month.map((r) => r.share_trades + r.sealed_trades)
  );
  return (
    <div className="space-y-4" data-testid="marketplace-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatCard label="Trades (all time)" value={`${totalTrades}`} />
        <StatCard label="Marketplace GMV (all time)" value={formatEur(totalGmv)} />
      </div>

      <Card>
        <CardContent className="p-5 space-y-3">
          <h3 className="font-heading font-semibold">
            Trades per month (opened-share vs sealed-dossier)
          </h3>
          {totalTrades === 0 ? (
            <p className="text-sm opacity-70 italic">
              No marketplace trades in the last 12 months yet.
            </p>
          ) : (
            <>
              <div className="space-y-1.5">
                {data.trades_by_month.map((row) => (
                  <div
                    key={row.month}
                    className="flex items-center gap-3"
                    title={`${row.month}: ${row.share_trades} share · ${row.sealed_trades} sealed`}
                  >
                    <span className="font-mono text-xs w-20 opacity-80">
                      {row.month}
                    </span>
                    <div className="flex-1 h-4 bg-brand-paper-100 rounded-sm overflow-hidden flex">
                      <div
                        style={{
                          width: `${(row.share_trades / maxMonthTrades) * 100}%`,
                        }}
                        className="h-full bg-brand-forest-700"
                        aria-label={`${row.share_trades} opened-share trades`}
                      />
                      <div
                        style={{
                          width: `${(row.sealed_trades / maxMonthTrades) * 100}%`,
                        }}
                        className="h-full bg-brand-forest-300"
                        aria-label={`${row.sealed_trades} sealed-dossier trades`}
                      />
                    </div>
                    <span className="font-mono text-xs w-24 text-right tabular-nums opacity-80">
                      {row.share_trades} / {row.sealed_trades}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 text-xs pt-1 opacity-80">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 bg-brand-forest-700 rounded-sm" />
                  Opened-share
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 bg-brand-forest-300 rounded-sm" />
                  Sealed-dossier
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h3 className="font-heading font-semibold mb-3">
            Cumulative by surface
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left opacity-70">
                <th className="py-1 pr-4 font-medium">Surface</th>
                <th className="py-1 pr-4 font-medium text-right">Trades</th>
                <th className="py-1 font-medium text-right">GMV</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <td className="py-1.5 pr-4">Opened-share</td>
                <td className="py-1.5 pr-4 text-right tabular-nums">
                  {c.share_trades}
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  {formatEur(c.share_gmv_eur_cents)}
                </td>
              </tr>
              <tr className="border-t border-border">
                <td className="py-1.5 pr-4">Sealed-dossier</td>
                <td className="py-1.5 pr-4 text-right tabular-nums">
                  {c.sealed_trades}
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  {formatEur(c.sealed_gmv_eur_cents)}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function TierTelemetryPanel({ data }: { data: TierTelemetryPanelData }) {
  if (data.portfolios.length === 0) {
    return (
      <EmptyState
        aria-label="No tier telemetry"
        description="No dossiers have been opened yet — configured-vs-actual tier distribution appears once reveals land."
      />
    );
  }
  return (
    <div className="space-y-4" data-testid="tier-telemetry-panel">
      {data.portfolios.map((p) => (
        <Card key={p.portfolio_id} data-testid="tier-telemetry-portfolio">
          <CardContent className="p-5">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
              <h3 className="font-heading font-semibold">{p.portfolio_title}</h3>
              <span className="text-xs font-mono opacity-70">
                {p.total_revealed} revealed
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left opacity-70">
                  <th className="py-1 pr-4 font-medium">Tier</th>
                  <th className="py-1 pr-4 font-medium text-right">
                    Configured
                  </th>
                  <th className="py-1 pr-4 font-medium text-right">Actual</th>
                  <th className="py-1 font-medium text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {p.tiers.map((t) => (
                  <tr
                    key={t.tier}
                    className="border-t border-border"
                    data-testid="tier-telemetry-row"
                  >
                    <td className="py-1.5 pr-4 capitalize">
                      {t.tier}
                      {/* Configured signature odds — unobtrusive secondary
                          line, dimmed to sit under the tier name. */}
                      <span className="block text-xs opacity-60 normal-case">
                        signed odds {formatPct(t.configured_signed_probability)}
                      </span>
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums opacity-80">
                      {formatPct(t.configured_probability)}
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums font-semibold">
                      {formatPct(t.revealed_fraction)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {t.revealed_count}
                      {/* Signed subset of the revealed count — same "(x)"
                          treatment the admin Drops SOLD column uses: dimmed,
                          shown only when > 0, with a plain hover title. */}
                      {t.signed_count > 0 ? (
                        <span
                          className="opacity-60"
                          title={`${t.signed_count} of ${t.revealed_count} signed`}
                        >
                          {" "}
                          ({t.signed_count})
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}
      <p className="text-xs opacity-60 italic leading-relaxed">{data.note}</p>
    </div>
  );
}

function FoundersMultiplePanel({
  data,
}: {
  data: FoundersMultiplePanelData;
}) {
  if (data.secondary_sales_count === 0) {
    return (
      <EmptyState
        aria-label="No Founder's resales"
        description="No Founder's-tier certificates have resold yet — the secondary-market multiple appears after the first Founder's resale settles."
      />
    );
  }
  return (
    <div className="space-y-4" data-testid="founders-multiple-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Secondary / primary multiple"
          value={data.multiple !== null ? `${data.multiple.toFixed(2)}×` : "—"}
        />
        <StatCard
          label="Founder's resales"
          value={`${data.secondary_sales_count}`}
        />
        <StatCard
          label="Avg secondary price"
          value={formatEur(data.avg_secondary_price_cents)}
        />
        <StatCard
          label="Avg primary (face) price"
          value={formatEur(data.avg_primary_price_cents)}
        />
      </div>
      <p className="text-xs opacity-60 italic leading-relaxed">
        Multiple = total Founder's-tier resale value ÷ total originating primary
        (face) value across {data.secondary_sales_count} settled resale
        {data.secondary_sales_count === 1 ? "" : "s"}.
      </p>
    </div>
  );
}

// Story 11.2, turned into EUROS by Story 11.9 (2026-09-02) — the VAT exposure
// that "accept payments from everywhere" creates, and what is set aside against
// it.
//
// A top-up from an unresearched country is ACCEPTED and charged 0% (owner ruling
// R8, 2026-09-01: no geo-restriction). ⛔ NOTHING ON THIS SCREEN BLOCKS A
// PAYMENT — it is a monitor and a trigger for research, never a gate.
//
// ⚑ THE UNIT IS EUROS, NOT PAYMENTS, and that is the point of the story. The
// owner's own backdating answer: a nil-threshold jurisdiction can claim VAT on
// ALL purchases ever taken from a country, retroactively and with no time limit
// — so the exposure grows with cumulative sales value and never expires. A
// payment count would say nothing about the size of the decision.
//
// Two halves on one screen, because it answers one question in two directions:
// what we have not looked at yet, and what we already owe and to whom.

/** The calendar day of an instant, in UTC. The AC asks for the DATE of the first
 *  payment; "18d ago" is a different fact and a worse one for a liability that
 *  never expires. */
function formatDay(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

function UnresearchedCountriesPanel({
  data,
}: {
  data: UnresearchedCountriesPanelData;
}) {
  const nothingAnywhere =
    data.countries.length === 0 && data.researched.length === 0;
  if (nothingAnywhere) {
    return (
      <EmptyState
        aria-label="No VAT exposure to report"
        description="No top-up has arrived from a country without a researched VAT treatment, and no jurisdiction is on file yet. A row appears here the moment one does — the payment is accepted and charged 0%, a reserve is set aside against it, and the country needs researching."
      />
    );
  }
  return (
    <div className="space-y-4" data-testid="unresearched-countries-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Unresearched top-up value"
          value={formatEur(data.total_gross_eur_cents)}
        />
        <StatCard
          label={`Worst case at known rates${
            data.unquantified_country_count > 0
              ? ` (+${data.unquantified_country_count} unquantified)`
              : ""
          }`}
          value={formatEur(data.total_worst_case_exposure_eur_cents)}
        />
        <StatCard
          label="Reserve held (every VAT hedge)"
          value={formatEur(data.total_provision_held_eur_cents)}
        />
        <StatCard
          label="Countries awaiting research"
          value={`${data.country_count} · ${data.total_topups} top-up${
            data.total_topups === 1 ? "" : "s"
          }`}
        />
      </div>

      {data.countries.length > 0 ? (
        <Card>
          <CardContent className="p-5">
            <h3 className="font-heading font-semibold mb-3">
              Unresearched — accepted, charged 0%, reserved against
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left opacity-70">
                    <th className="py-1 pr-4 font-medium">Country</th>
                    <th className="py-1 pr-4 font-medium text-right">
                      Gross taken
                    </th>
                    <th className="py-1 pr-4 font-medium text-right">Top-ups</th>
                    <th className="py-1 pr-4 font-medium">First payment</th>
                    <th className="py-1 pr-4 font-medium text-right">
                      Reserve held
                    </th>
                    <th className="py-1 pr-4 font-medium text-right">
                      Worst case
                    </th>
                    <th className="py-1 font-medium">Research goes in</th>
                  </tr>
                </thead>
                <tbody>
                  {data.countries.map((row) => (
                    <tr
                      key={row.country}
                      className="border-t border-border"
                      data-testid="unresearched-country-row"
                    >
                      <td className="py-1.5 pr-4 font-mono">
                        {row.country}
                        {row.register_status ? (
                          <>
                            {" "}
                            <Badge variant="secondary">
                              {row.register_status}
                            </Badge>
                          </>
                        ) : null}
                      </td>
                      <td className="py-1.5 pr-4 text-right tabular-nums font-semibold">
                        {formatEur(row.gross_eur_cents)}
                      </td>
                      <td className="py-1.5 pr-4 text-right tabular-nums">
                        {row.topup_count}
                      </td>
                      <td className="py-1.5 pr-4 tabular-nums">
                        {formatDay(row.first_seen_at)}
                      </td>
                      <td className="py-1.5 pr-4 text-right tabular-nums">
                        {formatEur(row.provision_held_eur_cents)}
                      </td>
                      <td
                        className="py-1.5 pr-4 text-right tabular-nums"
                        data-testid="worst-case-cell"
                      >
                        {row.worst_case_exposure_eur_cents === null ? (
                          // ⚑ AN HONEST "WE DO NOT KNOW WHAT THIS ONE COSTS" IS
                          // A REQUIRED OUTPUT, NOT A BLANK CELL. A zero here
                          // would read as "this one is free".
                          <Badge variant="secondary">unquantified</Badge>
                        ) : (
                          <>
                            {formatEur(row.worst_case_exposure_eur_cents)}
                            {row.indicative_rate_bps !== null ? (
                              <span className="opacity-60">
                                {" "}
                                @ {formatBps(row.indicative_rate_bps)}
                              </span>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td className="py-1.5 font-mono text-xs opacity-70">
                        {row.research_artifact_path}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs opacity-60 italic leading-relaxed mt-3">
              These top-ups were accepted and charged 0% VAT — deliberately.
              Against each one,{" "}
              {formatBps(data.provision_rate_bps)} of the gross is moved out of
              spendable and held here until the country's real rate is known;{" "}
              {formatEur(data.total_provision_released_eur_cents)} has been
              released so far across every VAT reserve, this rate and the
              own-rail ones both. Researching a country adds its file under
              docs/vat-jurisdictions/, and every subsequent payment from it is
              treated under that research; the ones already taken stay as
              written, and their reserve is released retroactively.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {data.researched.length > 0 ? (
        <Card>
          <CardContent className="p-5">
            <h3 className="font-heading font-semibold mb-3">
              Researched — what we owe, and where
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left opacity-70">
                    <th className="py-1 pr-4 font-medium">Country</th>
                    <th className="py-1 pr-4 font-medium">Status</th>
                    <th className="py-1 pr-4 font-medium">Reporting rail</th>
                    <th className="py-1 pr-4 font-medium text-right">Rate</th>
                    <th className="py-1 pr-4 font-medium text-right">
                      Reserve held
                    </th>
                    <th className="py-1 font-medium text-right">
                      VAT payable position
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.researched.map((row) => (
                    <tr
                      key={row.country}
                      className="border-t border-border"
                      data-testid="researched-country-row"
                    >
                      <td className="py-1.5 pr-4 font-mono">{row.country}</td>
                      <td className="py-1.5 pr-4">
                        {row.status ?? (
                          <span className="opacity-60">not on the register</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-4">
                        {row.reporting_rail ?? (
                          <span className="opacity-60">—</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-4 text-right tabular-nums">
                        {row.rate_bps === null ? (
                          // A `researched_special` row has no national rate and
                          // must never be shown carrying one — the United States
                          // is per-state nexus, not a VAT rate.
                          <span className="opacity-60">no single rate</span>
                        ) : (
                          <>
                            {formatBps(row.rate_bps)}
                            {row.at_or_above_provision_rate ? (
                              <>
                                {" "}
                                <Badge variant="warning">
                                  at/above reserve rate
                                </Badge>
                              </>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td className="py-1.5 pr-4 text-right tabular-nums">
                        {formatEur(row.provision_held_eur_cents)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums font-semibold">
                        {formatEur(row.vat_payable_eur_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs opacity-60 italic leading-relaxed mt-3">
              Positions are read from the ledger's per-member-state vat_payable
              accounts — never estimated. The UK reports on its own rail and the
              United States is per-state nexus rather than a rate, so both appear
              here in the shape their own research gives them. A jurisdiction on
              its own rail owes whether or not we are registered in Denmark, so
              every top-up from one is reserved against at that rail's own rate —
              that money is the reserve held beside it. A country flagged{" "}
              <span className="whitespace-nowrap">at/above reserve rate</span>{" "}
              came back at or higher than the{" "}
              {formatBps(data.provision_rate_bps)} its earlier payments were
              reserved at — those were under-provisioned, or reserved to the cent
              with nothing left over, and any difference was funded from platform
              revenue when the reserve was released.
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

// Story 11.3 — the VAT registration state, and the two counters that end it.
//
// ⛔ THE CONTROL AT THE BOTTOM IS THE ONLY WAY THE STATE EVER CHANGES. Not an
// environment variable, not a config file, not a deploy: an audited admin
// action that writes an append-only row with an effective date.
//
// Two counters and not one, because either can end the unregistered phase
// first: a platform whose buyers are mostly Danish trips DKK 50,000, one whose
// buyers are mostly German trips EUR 10,000. They are in DIFFERENT CURRENCIES,
// which is why the EUR->DKK rate is on the panel — it is a provisional setting,
// and a reader has to be able to see which number produced the Danish figure.

function formatMinorUnits(cents: number, currency: "DKK" | "EUR"): string {
  const whole = Math.floor(Math.abs(cents) / 100).toLocaleString("en-US");
  const rest = (Math.abs(cents) % 100).toString().padStart(2, "0");
  const symbol = currency === "EUR" ? "€" : "DKK ";
  return `${cents < 0 ? "-" : ""}${symbol}${whole}.${rest}`;
}

function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(1)}%`;
}

/// One counter: the figure, the threshold, and a meter with the alarm point
/// marked on it. The meter is here because a proportion is the thing being
/// judged, and a bare pair of numbers makes the reader do the division.
///
/// ⚑ EXPORTED FOR STORY 11.8, NOT MOVED. The close screen has to show both
/// threshold counters ("how close am I to having to register" is the same
/// question as "how much is in which bucket"), and it renders THIS component
/// against THIS endpoint's data rather than growing a second copy of the panel
/// logic. Its home stays here, with the rest of the VAT registration panel.
export function ThresholdCounterCard({ counter }: { counter: VatThresholdCounter }) {
  const fillPct = Math.min(100, counter.proportion_bps / 100);
  const alarmPct = Math.min(100, counter.alarm_at_bps / 100);
  const tone = counter.tripped
    ? "destructive"
    : counter.alarm_reached
      ? "warning"
      : "success";
  return (
    <Card data-testid={`vat-counter-${counter.kind}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs uppercase tracking-wide opacity-70">
            {counter.label}
          </p>
          <Badge variant={tone}>
            {counter.tripped
              ? "Threshold passed"
              : counter.alarm_reached
                ? "Early warning"
                : "Headroom"}
          </Badge>
        </div>

        <p className="text-2xl font-heading font-bold tabular-nums">
          {formatMinorUnits(counter.turnover_cents, counter.currency)}
          <span className="text-sm font-normal opacity-70">
            {" "}
            of {formatMinorUnits(counter.threshold_cents, counter.currency)}
          </span>
        </p>

        {/* Meter. The tick is the alarm point, so "how close" reads without
            arithmetic. Marked aria-hidden — every figure it encodes is stated
            in text beside it. */}
        <div
          className="relative h-2 w-full rounded-full bg-brand-forest-50 overflow-hidden"
          aria-hidden="true"
        >
          <div
            className="h-full rounded-full bg-brand-forest-900"
            style={{ width: `${fillPct}%` }}
          />
          <div
            className="absolute inset-y-0 w-px bg-brand-forest-900 opacity-60"
            style={{ left: `${alarmPct}%` }}
          />
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="opacity-70">Used</dt>
          <dd className="text-right tabular-nums">
            {formatBps(counter.proportion_bps)}
          </dd>
          <dt className="opacity-70">Headroom</dt>
          <dd className="text-right tabular-nums">
            {formatMinorUnits(counter.headroom_cents, counter.currency)}
          </dd>
          <dt className="opacity-70">
            Alarm at {formatBps(counter.alarm_at_bps)}
          </dt>
          <dd className="text-right tabular-nums">
            {formatMinorUnits(counter.alarm_at_cents, counter.currency)}
          </dd>
          {counter.currency === "DKK" && (
            <>
              <dt className="opacity-70">Stamped gross</dt>
              <dd className="text-right tabular-nums">
                {formatMinorUnits(counter.gross_eur_cents, "EUR")}
              </dd>
            </>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}

/// The audited admin action. Append-only: there is no edit and no delete, and a
/// wrong effective date is corrected by recording another row with the SAME
/// effective date — which is what the helper text says, because an admin who
/// does not know that will go looking for an edit button.
function VatRegistrationControl({
  current,
}: {
  current: VatRegistrationPanelData["current"];
}) {
  const mutation = useRecordVatRegistrationState();
  const [state, setState] = useState<"not_registered" | "registered">(
    current.state,
  );
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [number, setNumber] = useState(current.vat_registration_number ?? "");
  const [destinationFrom, setDestinationFrom] = useState(
    current.eu_destination_vat_from ?? "",
  );
  const [note, setNote] = useState("");

  const canSubmit = effectiveFrom.length > 0 && !mutation.isPending;

  function submit() {
    mutation.mutate(
      {
        state,
        effective_from: effectiveFrom,
        // ⛔ An unregistered seller has no number to state on a receipt, and
        // the server rejects one outright — so it is not even sent.
        vat_registration_number:
          state === "registered" && number.trim().length > 0
            ? number.trim()
            : null,
        eu_destination_vat_from:
          destinationFrom.length > 0 ? destinationFrom : null,
        note: note.trim().length > 0 ? note.trim() : null,
      },
      {
        onSuccess: () => {
          setNote("");
        },
      },
    );
  }

  return (
    <Card data-testid="vat-registration-control">
      <CardContent className="p-5 space-y-4">
        <div>
          <h3 className="font-heading font-semibold">
            Record a registration state
          </h3>
          <p className="text-sm opacity-70 leading-relaxed">
            Writes an audited, append-only row. Nothing here is ever edited or
            deleted: to correct a wrong effective date, record another row with
            that same date — the later entry becomes the one in force, and the
            first stays visible in the audit log.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="vat_state">State</Label>
            <select
              id="vat_state"
              className="block w-full rounded-md border border-brand-forest-200 bg-white p-2 text-sm"
              value={state}
              onChange={(e) =>
                setState(e.target.value as "not_registered" | "registered")
              }
            >
              <option value="not_registered">Not registered</option>
              <option value="registered">Registered</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vat_effective_from">Effective from</Label>
            <Input
              id="vat_effective_from"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vat_number">
              VAT registration number{" "}
              <span className="opacity-60 font-normal">
                {state === "registered" ? "(receipts need it)" : "(none)"}
              </span>
            </Label>
            <Input
              id="vat_number"
              value={state === "registered" ? number : ""}
              disabled={state === "not_registered"}
              placeholder="DK12345678"
              onChange={(e) => setNumber(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vat_destination_from">
              EU destination VAT from{" "}
              <span className="opacity-60 font-normal">(optional)</span>
            </Label>
            <Input
              id="vat_destination_from"
              type="date"
              value={destinationFrom}
              onChange={(e) => setDestinationFrom(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="vat_note">
            Note <span className="opacity-60 font-normal">(optional)</span>
          </Label>
          <Textarea
            id="vat_note"
            rows={2}
            value={note}
            placeholder="Why this row exists — e.g. supersedes the row recorded on the 3rd, wrong effective date."
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {mutation.isError && (
          <p
            className="text-sm text-state-error-fg"
            role="alert"
            data-testid="vat-registration-error"
          >
            {mutation.error.message}
          </p>
        )}
        {mutation.isSuccess && (
          <p className="text-sm opacity-80" data-testid="vat-registration-ok">
            Recorded. Effective from {mutation.data.data.effective_from}; audit
            row {mutation.data.data.audit_log_id}.
          </p>
        )}

        <Button variant="primary" onClick={submit} disabled={!canSubmit}>
          {mutation.isPending ? "Recording…" : "Record state"}
        </Button>
      </CardContent>
    </Card>
  );
}

function VatRegistrationPanel({ data }: { data: VatRegistrationPanelData }) {
  const { current, counters } = data;
  const registered = current.state === "registered";
  return (
    <div className="space-y-4" data-testid="vat-registration-panel">
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Badge variant={registered ? "success" : "secondary"}>
                {registered ? "VAT registered" : "Not VAT registered"}
              </Badge>
              <p className="text-sm opacity-80">
                {current.effective_from
                  ? `Effective from ${current.effective_from}`
                  : "No state has ever been recorded — which is what “not registered” means here."}
              </p>
            </div>
            {current.recorded_at && (
              <p className="text-xs opacity-60">
                Recorded {formatRelative(current.recorded_at)}
              </p>
            )}
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <dt className="opacity-70">VAT registration number</dt>
            <dd className="font-mono">
              {current.vat_registration_number ?? "—"}
            </dd>
            <dt className="opacity-70">EU destination VAT from</dt>
            <dd className="font-mono">
              {current.eu_destination_vat_from ?? "— (Danish-placed)"}
            </dd>
          </dl>

          {current.note && (
            <p className="text-sm opacity-80 italic">{current.note}</p>
          )}

          <p className="text-xs opacity-60 leading-relaxed">
            The treatment of a top-up is fixed at the moment it is bought.
            Changing the state here never restates a top-up already taken —
            credit bought while unregistered stays untaxed even when it is spent
            afterwards.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ThresholdCounterCard counter={counters.danish_domestic} />
        <ThresholdCounterCard counter={counters.eu_cross_border} />
      </div>

      <Card>
        <CardContent className="p-5">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="opacity-70">Counting year</dt>
            <dd className="text-right tabular-nums">{counters.year}</dd>
            <dt className="opacity-70">
              EUR → DKK rate used{" "}
              <span className="opacity-70">(provisional setting)</span>
            </dt>
            <dd className="text-right tabular-nums">
              {(counters.eur_to_dkk_rate_bps / 10_000).toFixed(4)}
            </dd>
            <dt className="opacity-70">
              Top-ups in neither counter{" "}
              <span className="opacity-70">(no EU country on the stamp)</span>
            </dt>
            <dd className="text-right tabular-nums">
              {formatMinorUnits(
                counters.unattributed_gross_eur_cents,
                "EUR",
              )}
            </dd>
          </dl>
          <p className="text-xs opacity-60 italic leading-relaxed mt-3">
            Counter definitions — gross, calendar year, all supplies — are the
            conservative reading and are provisional on accountant question W21.
            Turnover with no EU country on its stamp is shown separately and is
            never folded into either counter.
          </p>
        </CardContent>
      </Card>

      <VatRegistrationControl current={current} />
    </div>
  );
}

type PanelKey =
  | "users"
  | "drops"
  | "marketplace"
  | "creator_earnings"
  | "reconciliation"
  | "tier_telemetry"
  | "founders_multiple"
  | "unresearched_countries"
  | "vat_registration";

const PANELS: { key: PanelKey; label: string }[] = [
  { key: "users", label: "Users" },
  { key: "drops", label: "Drops" },
  { key: "marketplace", label: "Marketplace" },
  { key: "creator_earnings", label: "Creator earnings" },
  { key: "reconciliation", label: "Reconciliation" },
  { key: "tier_telemetry", label: "Tier telemetry" },
  { key: "founders_multiple", label: "Founder's multiple" },
  { key: "unresearched_countries", label: "VAT exposure" },
  { key: "vat_registration", label: "VAT registration" },
];

/// Story 11.9 — the panel named by `#<panel_key>` in the URL, or Users.
///
/// This is what makes Story 11.2's first-payment alert a real LINK rather than a
/// gesture at one: the alert says `/admin/metrics#unresearched_countries`
/// (`country_register_sync::UNRESEARCHED_EXPOSURE_SURFACE_PATH`) and the reader
/// lands on the exposure panel instead of on Users with a hash nothing reads.
/// Read once, at mount, on purpose — a hash change mid-session is the user
/// clicking a tab, and re-driving the tab from it would fight them.
function initialPanel(): PanelKey {
  if (typeof window === "undefined") return "users";
  const fromHash = window.location.hash.replace(/^#/, "");
  return PANELS.some((p) => p.key === fromHash)
    ? (fromHash as PanelKey)
    : "users";
}

export function AdminMetricsPage() {
  const query = useAdminMetrics();
  const [defaultPanel] = useState<PanelKey>(initialPanel);

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
      <div className="max-w-6xl mx-auto space-y-[var(--gh-rhythm-admin,16px)]">
        <header className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-heading font-bold">
              Platform metrics
            </h1>
            <p className="opacity-70 text-sm">
              Refreshes on page-load. No realtime stream at v1.
            </p>
          </div>
          {query.data ? (
            <p className="text-xs font-mono opacity-70 self-end">
              updated {formatRelative(query.data.data.generated_at)} · window{" "}
              {query.data.data.window.months}mo
            </p>
          ) : null}
        </header>

        {query.isPending ? (
          <Card>
            <CardContent className="p-8 text-center opacity-70">
              Loading metrics…
            </CardContent>
          </Card>
        ) : query.isError ? (
          <Card>
            <CardContent className="p-6 text-center space-y-3">
              <p className="text-destructive">
                {query.error.message ?? "Failed to load metrics."}
              </p>
              <Button onClick={() => query.refetch()} variant="secondary">
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue={defaultPanel} className="w-full">
            <TabsList className="overflow-x-auto overflow-y-hidden whitespace-nowrap">
              {PANELS.map((p) => (
                <TabsTrigger key={p.key} value={p.key}>
                  {p.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Users — wired */}
            <TabsContent value="users">
              {isWired(query.data.data.users) ? (
                <UsersPanel
                  data={query.data.data.users.data as UsersPanelData}
                />
              ) : (
                <PlaceholderPanel
                  reason={query.data.data.users.unavailable_reason}
                  story={query.data.data.users.populates_in_story}
                />
              )}
            </TabsContent>

            {/* Creator earnings — wired 2026-08-03 */}
            <TabsContent value="creator_earnings">
              {isWired(query.data.data.creator_earnings) ? (
                <CreatorEarningsPanel
                  data={query.data.data.creator_earnings.data}
                />
              ) : (
                <PlaceholderPanel
                  reason={query.data.data.creator_earnings.unavailable_reason}
                  story={query.data.data.creator_earnings.populates_in_story}
                />
              )}
            </TabsContent>

            {/* Reconciliation — wired 2026-08-14 (Story 6.7) */}
            <TabsContent value="reconciliation">
              {isWired(query.data.data.reconciliation) ? (
                <ReconciliationPanel
                  data={query.data.data.reconciliation.data}
                />
              ) : (
                <PlaceholderPanel
                  reason={query.data.data.reconciliation.unavailable_reason}
                  story={query.data.data.reconciliation.populates_in_story}
                />
              )}
            </TabsContent>

            {/* Drops — wired 2026-08-14 */}
            <TabsContent value="drops">
              {isWired(query.data.data.drops) ? (
                <DropsPanel data={query.data.data.drops.data} />
              ) : (
                <PlaceholderPanel
                  reason={query.data.data.drops.unavailable_reason}
                  story={query.data.data.drops.populates_in_story}
                />
              )}
            </TabsContent>

            {/* Marketplace — wired 2026-08-14 */}
            <TabsContent value="marketplace">
              {isWired(query.data.data.marketplace) ? (
                <MarketplacePanel data={query.data.data.marketplace.data} />
              ) : (
                <PlaceholderPanel
                  reason={query.data.data.marketplace.unavailable_reason}
                  story={query.data.data.marketplace.populates_in_story}
                />
              )}
            </TabsContent>

            {/* Tier telemetry — wired 2026-08-14 */}
            <TabsContent value="tier_telemetry">
              {isWired(query.data.data.tier_telemetry) ? (
                <TierTelemetryPanel
                  data={query.data.data.tier_telemetry.data}
                />
              ) : (
                <PlaceholderPanel
                  reason={query.data.data.tier_telemetry.unavailable_reason}
                  story={query.data.data.tier_telemetry.populates_in_story}
                />
              )}
            </TabsContent>

            {/* Founder's multiple — wired 2026-08-14 */}
            <TabsContent value="founders_multiple">
              {isWired(query.data.data.founders_multiple) ? (
                <FoundersMultiplePanel
                  data={query.data.data.founders_multiple.data}
                />
              ) : (
                <PlaceholderPanel
                  reason={query.data.data.founders_multiple.unavailable_reason}
                  story={query.data.data.founders_multiple.populates_in_story}
                />
              )}
            </TabsContent>

            {/* Unresearched countries — wired 2026-09-01 (Story 11.2) */}
            <TabsContent value="unresearched_countries">
              {isWired(query.data.data.unresearched_countries) ? (
                <UnresearchedCountriesPanel
                  data={query.data.data.unresearched_countries.data}
                />
              ) : (
                <PlaceholderPanel
                  reason={
                    query.data.data.unresearched_countries.unavailable_reason
                  }
                  story={
                    query.data.data.unresearched_countries.populates_in_story
                  }
                />
              )}
            </TabsContent>

            {/* VAT registration — wired 2026-09-01 (Story 11.3) */}
            <TabsContent value="vat_registration">
              {isWired(query.data.data.vat_registration) ? (
                <VatRegistrationPanel
                  data={query.data.data.vat_registration.data}
                />
              ) : (
                <PlaceholderPanel
                  reason={query.data.data.vat_registration.unavailable_reason}
                  story={query.data.data.vat_registration.populates_in_story}
                />
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </main>
  );
}
